import { useRef, useState, useCallback, useEffect } from "react";
import { useEventListeners, type EventListeners } from "@/hooks/useEventListeners";
import { getToken } from "@/lib/keycloak";
import { synthesize } from "./ttsenClient";
import type { TTSChunk, WordCue, WordCueEvent } from "./types";

export type TTSPlaybackEvents = { wordCue: (event: WordCueEvent) => void };

/**
 * PLAYBACK — the second half of the pipeline: request audio → queue audio →
 * play audio. It knows NOTHING about the data-stream bus, captures, or
 * tokenization. Its only input is finalized {@link TTSChunk}s handed to
 * `enqueueChunk`; its only outputs are sound, `wordCue` events, and the
 * `isPlaying` / `currentEntryId` React state.
 *
 * Design (keyed, deduped, bounded-concurrency, ordered):
 *   - Chunks are deduped by `${unitKey}:${baseOffset}` (Dexie content identity
 *     + byte offset) so the same span is never synthesized twice.
 *   - A bounded pool keeps up to MAX_CONCURRENT ttsen requests in flight and
 *     refills as each returns ("send 5, refill on each return").
 *   - Each request synthesizes to a COMPLETE AudioBuffer tagged with a
 *     monotonic `seq`; a scheduler lays buffers on the playhead in strict `seq`
 *     order regardless of completion order → ordered + gapless.
 *   - A single requestAnimationFrame audio-clock loop derives the active
 *     karaoke word and `currentEntryId` from `ctx.currentTime`. No wall timers.
 */
export function useTTSPlayback() {
  const events = useEventListeners<TTSPlaybackEvents>("tts");

  const MAX_CONCURRENT = 5;

  // --- Audio context + scheduled nodes --------------------------------------
  const audioCtxRef = useRef<AudioContext | null>(null);
  const abortRef = useRef<AbortController>(new AbortController());
  const playHeadRef = useRef<number>(0);
  const activeNodesRef = useRef<AudioBufferSourceNode[]>([]);

  // --- The synthesis pipeline ------------------------------------------------
  type QueuedChunk = TTSChunk & { seq: number };
  type SynthResult = { chunk: QueuedChunk; audioBuf: AudioBuffer | null; cues: WordCue[] };

  const pendingRef = useRef<QueuedChunk[]>([]);        // accepted, not yet dispatched (seq order)
  const queuedKeysRef = useRef<Set<string>>(new Set()); // dedup by chunk key
  const seqCounterRef = useRef(0);                      // next seq to assign
  const inFlightRef = useRef(0);                        // concurrent ttsen requests
  const readyRef = useRef<Map<number, SynthResult>>(new Map()); // completed, awaiting scheduling
  const scheduleSeqRef = useRef(0);                     // next seq to schedule (order-preserving)
  const producersRef = useRef(0);                       // external producers still able to emit

  const chunkKey = (c: TTSChunk) => `${c.unitKey}:${c.baseOffset}`;

  // --- Scheduled timeline data (read by the audio-clock loop) ---------------
  type ScheduledSpan = { start: number; end: number; entryId: string };
  const spansRef = useRef<ScheduledSpan[]>([]);
  type ScheduledCue = { start: number; end: number; si: number; ei: number; t: string; entryId: string };
  const pendingCuesRef = useRef<ScheduledCue[]>([]);
  const clockRafRef = useRef<number | null>(null);
  const lastCueRef = useRef<ScheduledCue | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  // The entry whose audio is playing NOW, derived by the audio-clock loop.
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  // The entry the USER just asked to play, set synchronously on click BEFORE
  // any resolve/fetch/synthesis. Lights the button immediately; the audio clock
  // takes over once real playback begins.
  const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
  const currentEntryId = activeEntryId ?? pendingEntryId;

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext({ sampleRate: 24000 });
    }
    if (audioCtxRef.current.state === "suspended") {
      void audioCtxRef.current.resume();
    }
  }, []);

  const waitForAudioDrain = useCallback((): Promise<void> => {
    if (activeNodesRef.current.length === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const last = activeNodesRef.current[activeNodesRef.current.length - 1];
      if (!last) { resolve(); return; }
      last.addEventListener("ended", () => {
        if (activeNodesRef.current.length === 0) resolve();
        else void waitForAudioDrain().then(resolve);
      }, { once: true });
    });
  }, []);

  // --- Loading pulse: audible "voice is coming" cue during the queued gap ---
  // A soft blip synthesized on the shared AudioContext (no asset, no fetch).
  // Repeats while audio is requested but not yet playing, so the user gets
  // feedback even when looking away. Stops the instant real audio schedules.
  const playBlip = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    // Round tone that swells and fades — a soft pulse, not a droplet. A4 (440Hz).
    osc.frequency.setValueAtTime(440, now);
    // Symmetric envelope: gentle ~90ms swell in, ~250ms fade out. No sharp
    // transient, so it reads as a breathing pulse rather than a plink.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.09, now + 0.09);
    gain.gain.linearRampToValueAtTime(0, now + 0.34);

    // Echo: a feedback delay so the pulse trails off in soft repeats.
    const delay = ctx.createDelay();
    delay.delayTime.setValueAtTime(0.18, now);
    const feedback = ctx.createGain();
    feedback.gain.setValueAtTime(0.4, now);

    osc.connect(gain);
    gain.connect(ctx.destination);
    // Dry signal into the delay line, which feeds back into itself for repeats.
    gain.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.36);
  }, []);

  // Pulse while queued: audio has been requested (isPlaying) but nothing is
  // actually playing yet (activeEntryId === null). Matches the loading spinner.
  const isQueued = isPlaying && activeEntryId === null;
  useEffect(() => {
    if (!isQueued) return;
    playBlip();
    const id = window.setInterval(playBlip, 2000);
    return () => window.clearInterval(id);
  }, [isQueued, playBlip]);

  // --- Audio-clock loop: the single playback driver -------------------------
  const startClockLoop = useCallback(() => {
    if (clockRafRef.current !== null) return;
    const tick = () => {
      const ctx = audioCtxRef.current;
      if (!ctx) { clockRafRef.current = null; return; }
      const now = ctx.currentTime;

      // Karaoke: emit the single active word cue.
      while (pendingCuesRef.current.length && pendingCuesRef.current[0].end < now) {
        pendingCuesRef.current.shift();
      }
      const activeCue = pendingCuesRef.current.find((c) => c.start <= now && now < c.end);
      if (activeCue && activeCue !== lastCueRef.current) {
        lastCueRef.current = activeCue;
        // Carry the cue's absolute audio time (ms) so the highlighter can detect
        // when the highlight has fallen behind the audio and trigger recovery.
        events.emit.wordCue({
          entryId: activeCue.entryId,
          cues: [{
            t: activeCue.t,
            s: Math.round(activeCue.start * 1000),
            e: Math.round(activeCue.end * 1000),
            si: activeCue.si,
            ei: activeCue.ei,
          }],
        });
      }

      // Active span: derive currentEntryId.
      while (spansRef.current.length && spansRef.current[0].end <= now) {
        spansRef.current.shift();
      }
      const activeSpan = spansRef.current.find((s) => s.start <= now && now < s.end);
      if (activeSpan) {
        setActiveEntryId((prev) => {
          if (prev === activeSpan.entryId) return prev;
          return activeSpan.entryId;
        });
      }

      const moreWork = pendingCuesRef.current.length > 0 || spansRef.current.length > 0;
      clockRafRef.current = moreWork ? requestAnimationFrame(tick) : null;
    };
    clockRafRef.current = requestAnimationFrame(tick);
  }, [events]);

  // --- Synthesize ONE chunk to a complete AudioBuffer (concurrent-safe) -----
  const synthChunkToBuffer = useCallback(async (chunk: QueuedChunk): Promise<SynthResult> => {
    const accessToken = getToken();
    const ctx = audioCtxRef.current;
    if (!accessToken || !ctx) return { chunk, audioBuf: null, cues: [] };

    const pcmParts: Float32Array[] = [];
    let total = 0;
    let leftover = new Uint8Array(0);
    const cues: WordCue[] = [];

    const onAudio = (bytes: Uint8Array) => {
      const combined = new Uint8Array(leftover.length + bytes.length);
      combined.set(leftover);
      combined.set(bytes, leftover.length);
      const usable = combined.length - (combined.length % 4);
      leftover = combined.slice(usable);
      if (usable === 0) return;
      const f32 = new Float32Array(combined.slice(0, usable).buffer);
      pcmParts.push(f32);
      total += f32.length;
    };

    await synthesize({
      input: chunk.text,
      voice: chunk.voice,
      accessToken,
      signal: abortRef.current.signal,
      onAudio,
      onWordCues: (cs) => { for (const c of cs) cues.push(c); },
    });

    if (total === 0) return { chunk, audioBuf: null, cues };
    const merged = new Float32Array(total);
    let off = 0;
    for (const part of pcmParts) { merged.set(part, off); off += part.length; }
    const audioBuf = ctx.createBuffer(1, merged.length, 24000);
    audioBuf.copyToChannel(merged, 0);
    return { chunk, audioBuf, cues };
  }, []);

  // --- Place a completed result on the timeline (called in strict seq order) -
  const scheduleResult = useCallback((result: SynthResult) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const { chunk, audioBuf, cues } = result;

    // End-of-stream sentinel: no audio. Reached here in strict seq order, so
    // every real chunk is already scheduled. Wait for that audio to finish,
    // then — if nothing new arrived meanwhile — clear state + the highlight.
    if (chunk.done) {
      void waitForAudioDrain().then(() => {
        const idle =
          pendingRef.current.length === 0 &&
          inFlightRef.current === 0 &&
          readyRef.current.size === 0 &&
          activeNodesRef.current.length === 0;
        if (idle) {
          setIsPlaying(false);
          setPendingEntryId(null);
          setActiveEntryId(null);
          lastCueRef.current = null;
          events.emit.wordCue({ entryId: "", cues: [] });
        }
      });
      return;
    }

    if (!audioBuf) return;

    const node = ctx.createBufferSource();
    node.buffer = audioBuf;
    node.connect(ctx.destination);
    const startAt = Math.max(playHeadRef.current, ctx.currentTime);
    node.start(startAt);

    spansRef.current.push({ start: startAt, end: startAt + audioBuf.duration, entryId: chunk.rootEntryId });
    for (const c of cues) {
      pendingCuesRef.current.push({
        start: startAt + c.s / 1000,
        end: startAt + c.e / 1000,
        si: c.si + chunk.baseOffset,
        ei: c.ei + chunk.baseOffset,
        t: c.t,
        entryId: chunk.entryId,
      });
    }
    pendingCuesRef.current.sort((a, b) => a.start - b.start);

    playHeadRef.current = startAt + audioBuf.duration;
    activeNodesRef.current.push(node);
    node.onended = () => {
      activeNodesRef.current = activeNodesRef.current.filter((n) => n !== node);
    };
    startClockLoop();
  }, [startClockLoop, waitForAudioDrain, events]);

  // --- Drain completed results in seq order ---------------------------------
  const drainReady = useCallback(() => {
    while (readyRef.current.has(scheduleSeqRef.current)) {
      const seq = scheduleSeqRef.current;
      const result = readyRef.current.get(seq)!;
      readyRef.current.delete(seq);
      scheduleResult(result);
      scheduleSeqRef.current++;
    }
  }, [scheduleResult]);

  // --- Idle check: clear "speaking" once everything has played --------------
  const maybeFinish = useCallback(() => {
    if (
      producersRef.current === 0 &&
      pendingRef.current.length === 0 &&
      inFlightRef.current === 0 &&
      readyRef.current.size === 0
    ) {
      void waitForAudioDrain().then(() => {
        if (
          producersRef.current === 0 &&
          pendingRef.current.length === 0 &&
          inFlightRef.current === 0 &&
          readyRef.current.size === 0 &&
          activeNodesRef.current.length === 0
        ) {
          setIsPlaying(false);
          setPendingEntryId(null);
          setActiveEntryId(null);
        }
      });
    }
  }, [waitForAudioDrain]);

  // --- The concurrency pool: 5 in flight, refill on each return -------------
  const pump = useCallback(() => {
    const signal = abortRef.current.signal;
    while (!signal.aborted && inFlightRef.current < MAX_CONCURRENT && pendingRef.current.length > 0) {
      const chunk = pendingRef.current.shift()!;
      // The done sentinel carries no audio — skip synthesis and hand it to the
      // ordered scheduler so it lands strictly behind every real chunk.
      if (chunk.done) {
        readyRef.current.set(chunk.seq, { chunk, audioBuf: null, cues: [] });
        drainReady();
        continue;
      }
      inFlightRef.current++;
      setIsPlaying(true);
      void synthChunkToBuffer(chunk)
        .then((result) => {
          if (signal.aborted) return;
          readyRef.current.set(chunk.seq, result);
          drainReady();
        })
        .catch((err: unknown) => {
          if (!(err instanceof Error && err.name === "AbortError")) {
            console.error("[TTS] Synthesis error:", err);
          }
          if (!signal.aborted) {
            readyRef.current.set(chunk.seq, { chunk, audioBuf: null, cues: [] });
            drainReady();
          }
        })
        .finally(() => {
          inFlightRef.current--;
          pump();
          maybeFinish();
        });
    }
    maybeFinish();
  }, [synthChunkToBuffer, drainReady, maybeFinish]);

  // ==========================================================================
  // Public API
  // ==========================================================================

  /** Accept a finalized chunk: dedupe by stable key, assign seq, enqueue, pump. */
  const enqueueChunk = useCallback((chunk: TTSChunk) => {
    const seq = seqCounterRef.current++;
    // The done sentinel is never deduped (each turn emits its own) and carries
    // no synthesizable text — it just rides the ordered queue.
    if (chunk.done) {
      pendingRef.current.push({ ...chunk, seq });
      pump();
      return;
    }
    const key = chunkKey(chunk);
    if (queuedKeysRef.current.has(key)) {
      return; // already accepted — never synthesize the same span twice
    }
    queuedKeysRef.current.add(key);
    pendingRef.current.push({ ...chunk, seq });
    pump();
  }, [pump]);

  /** Register/retire an external producer (a capture) that may emit chunks. */
  const addProducer = useCallback(() => { producersRef.current += 1; }, []);
  const removeProducer = useCallback(() => {
    producersRef.current = Math.max(0, producersRef.current - 1);
    maybeFinish();
  }, [maybeFinish]);

  /** Prepare to play: ensure audio ctx + a fresh abort signal if needed. */
  const beginSession = useCallback((interrupt: boolean) => {
    if (interrupt) {
      stopPlaybackRef.current();
    }
    ensureAudioContext();
    if (interrupt || abortRef.current.signal.aborted) {
      abortRef.current = new AbortController();
    }
    setIsPlaying(true);
  }, [ensureAudioContext]);

  /** The abort signal for the current session (producers use it to bail). */
  const getSignal = useCallback(() => abortRef.current.signal, []);

  /** Set the intent entry so the button lights immediately on click. */
  const setIntentEntry = useCallback((entryId: string | null) => setPendingEntryId(entryId), []);

  const stopPlaybackRef = useRef<() => void>(() => {});
  const stopPlayback = useCallback(() => {
    abortRef.current?.abort();
    pendingRef.current = [];
    queuedKeysRef.current.clear();
    readyRef.current.clear();
    seqCounterRef.current = 0;
    scheduleSeqRef.current = 0;
    inFlightRef.current = 0;
    producersRef.current = 0;
    for (const node of activeNodesRef.current) {
      try { node.stop(); } catch { /* already ended */ }
      node.disconnect();
    }
    activeNodesRef.current = [];
    playHeadRef.current = 0;
    spansRef.current = [];
    if (clockRafRef.current !== null) {
      cancelAnimationFrame(clockRafRef.current);
      clockRafRef.current = null;
    }
    pendingCuesRef.current = [];
    lastCueRef.current = null;
    setIsPlaying(false);
    setPendingEntryId(null);
    setActiveEntryId(null);
    events.emit.wordCue({ entryId: "", cues: [] });
  }, [events]);
  stopPlaybackRef.current = stopPlayback;

  /** Close the audio context (unmount cleanup). */
  const dispose = useCallback(() => {
    stopPlayback();
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
  }, [stopPlayback]);

  return {
    // outputs
    isPlaying,
    currentEntryId,
    // The entry whose audio is playing NOW (null during the queued/loading gap).
    playingEntryId: activeEntryId,
    events: events as EventListeners<TTSPlaybackEvents>,
    // inputs
    enqueueChunk,
    addProducer,
    removeProducer,
    beginSession,
    getSignal,
    setIntentEntry,
    stopPlayback,
    dispose,
  };
}
